import React from "react";
import type { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import useLoginStore from "@/stores/loginStore";
import { useQueryGetMe } from "@/apis/users/getMe";
import * as Sentry from "@sentry/react";
import { IS_PRODUCTION } from "@/utils/env";
import { Avatar } from "@/components/avatar";
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "@/components/dropdown";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from "@/components/navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarDivider,
  SidebarFooter,
  SidebarHeader,
  SidebarHeading,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "@/components/sidebar";
import { SidebarLayout } from "@/components/sidebar-layout";
import {
  ArrowRightStartOnRectangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  LightBulbIcon,
  PlusIcon,
  ShieldCheckIcon,
  UserIcon,
} from "@heroicons/react/16/solid";
import {
  Cog6ToothIcon,
  HomeIcon,
  InboxIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  Square2StackIcon,
  TicketIcon,
} from "@heroicons/react/20/solid";
import { Heading } from "../heading";
import { FEATURES } from "@/config/features";

interface PageContainerProps {
  children: ReactNode;
  wide?: boolean;
}

const PageContainer: React.FC<PageContainerProps> = ({
  children,
  wide = false,
}) => {
  const navigate = useNavigate();
  const { clearToken } = useLoginStore();
  const { data: user, isLoading } = useQueryGetMe();

  // 사용자 정보를 Sentry에 업데이트
  React.useEffect(() => {
    if (IS_PRODUCTION && user) {
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.name,
        role: user.role,
        // 추가 정보
        grade: user.grade,
        school: user.school,
        status: user.status,
      });
    }
  }, [user]);

  const handleLogout = (e: React.MouseEvent) => {
    e.preventDefault();
    clearToken();
    navigate("/login");
  };

  return (
    <SidebarLayout
      wide={wide}
      navbar={
        <Navbar>
          <NavbarSpacer />
          <NavbarSection>
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                {user && (
                  <Avatar
                    initials={user?.name?.charAt(0)}
                    alt={user?.name}
                    className="w-20 h-20 cursor-pointer"
                  />
                )}
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="bottom end">
                <DropdownItem href="#" onClick={handleLogout}>
                  <ArrowRightStartOnRectangleIcon />
                  <DropdownLabel>Sign out</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar>
          <SidebarBody>
            <SidebarSection>
              <div className="flex items-center justify-between">
                <SidebarHeading>Exam</SidebarHeading>
                {!IS_PRODUCTION && (
                  <button
                    onClick={() => navigate("/exam_configs")}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer"
                    title="Exam Settings"
                  >
                    <Cog8ToothIcon className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                  </button>
                )}
              </div>
              <SidebarItem href="/exam_papers">
                <HomeIcon />
                <SidebarLabel>문제지 생성</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/exam_assignments">
                <Square2StackIcon />
                <SidebarLabel>시험 부여</SidebarLabel>
              </SidebarItem>
              {FEATURES.ShowExamResultPage && (
                <SidebarItem href="/exam_results">
                  <TicketIcon />
                  <SidebarLabel>시험 결과</SidebarLabel>
                </SidebarItem>
              )}
              <SidebarDivider />
              <SidebarHeading>Manage Students</SidebarHeading>
              <SidebarItem href="/users">
                <TicketIcon />
                <SidebarLabel>학생 목록</SidebarLabel>
              </SidebarItem>
              <SidebarItem href="/groups">
                <Cog6ToothIcon />
                <SidebarLabel>클래스 목록</SidebarLabel>
              </SidebarItem>
            </SidebarSection>
            <SidebarSpacer />
          </SidebarBody>
          <SidebarFooter className="max-lg:hidden">
            <Dropdown>
              <DropdownButton as={SidebarItem}>
                <span className="flex min-w-0 items-center gap-3">
                  {user?.name && (
                    <Avatar
                      initials={user.name.charAt(0)}
                      alt={user.name}
                      className="w-8 h-8 mr-2"
                    />
                  )}
                  <span className="min-w-0">
                    <span className="block truncate text-sm/5 font-medium text-zinc-950 dark:text-white">
                      {user?.name}
                    </span>
                    <span className="block truncate text-xs/5 font-normal text-zinc-500 dark:text-zinc-400">
                      {user?.email}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon />
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="top start">
                <DropdownItem href="#" onClick={handleLogout}>
                  <ArrowRightStartOnRectangleIcon />
                  <DropdownLabel>Sign out</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
    >
      {/* The page content */}
      <div className="">{children}</div>
    </SidebarLayout>
  );
};

export default PageContainer;
